<template>
  <footer class="footer-section">
    <div class="footer-main">
      <div class="footer-container">
        <div class="footer-grid">
          <!-- Company Info -->
          <div class="footer-col footer-brand">
            <div class="footer-logo-row">
              <img :src="siteLogo" alt="Franchise Brigade Logo" class="footer-logo" />
              <span class="footer-brand-name">{{ siteName }}</span>
            </div>
            <p class="footer-tagline">
              {{ siteDescription }}
            </p>
            <div class="footer-social">
              <a v-if="facebook && facebook !== '#'" :href="facebook" class="social-link" target="_blank" rel="noopener"><i class="fa-brands fa-facebook-f"></i></a>
              <a v-if="twitter && twitter !== '#'" :href="twitter" class="social-link" target="_blank" rel="noopener"><i class="fa-brands fa-twitter"></i></a>
              <a v-if="linkedin && linkedin !== '#'" :href="linkedin" class="social-link" target="_blank" rel="noopener"><i class="fa-brands fa-linkedin-in"></i></a>
              <a v-if="instagram && instagram !== '#'" :href="instagram" class="social-link" target="_blank" rel="noopener"><i class="fa-brands fa-instagram"></i></a>
            </div>
          </div>
          
          <!-- Quick Links -->
          <div class="footer-col">
            <h4 class="footer-heading">Quick Links</h4>
            <ul class="footer-links">
              <li><router-link to="/" @click="scrollToTop"><i class="fa-solid fa-home"></i> Home</router-link></li>
              <li><router-link to="/franchises" @click="scrollToTop"><i class="fa-solid fa-store"></i> Browse Franchises</router-link></li>
              <li><router-link to="/aboutus" @click="scrollToTop"><i class="fa-solid fa-users"></i> About Us</router-link></li>
              <li><router-link to="/blogs" @click="scrollToTop"><i class="fa-solid fa-blog"></i> Blog</router-link></li>
              <li><router-link to="/contactus" @click="scrollToTop"><i class="fa-solid fa-envelope"></i> Contact Us</router-link></li>
            </ul>
          </div>
          
          <!-- Franchise Categories -->
          <div class="footer-col">
            <h4 class="footer-heading">Franchise Categories</h4>
            <ul class="footer-links">
              <li><a href="/franchises?category=food"><i class="fa-solid fa-utensils"></i> Food & Beverage</a></li>
              <li><a href="/franchises?category=retail"><i class="fa-solid fa-shopping-bag"></i> Retail</a></li>
              <li><a href="/franchises?category=services"><i class="fa-solid fa-tools"></i> Services</a></li>
              <li><a href="/franchises?category=education"><i class="fa-solid fa-graduation-cap"></i> Education</a></li>
              <li><a href="/franchises?category=health"><i class="fa-solid fa-heart-pulse"></i> Health & Wellness</a></li>
              <li><a href="/franchises?category=automobile"><i class="fa-solid fa-car"></i> Automobile</a></li>
            </ul>
          </div>
          
          <!-- Contact Info -->
          <div class="footer-col">
            <h4 class="footer-heading">Contact Information</h4>
            <ul class="footer-contact">
              <li>
                <i class="fa-solid fa-location-dot contact-icon"></i>
                <div class="contact-text">
                  {{ contactAddress }}
                </div>
              </li>
              <li>
                <i class="fa-solid fa-phone contact-icon"></i>
                <div class="contact-text">
                  <a :href="`tel:${contactPhone}`">{{ contactPhone }}</a>
                </div>
              </li>
              <li>
                <i class="fa-solid fa-envelope contact-icon"></i>
                <div class="contact-text">
                  <a :href="`mailto:${contactEmail}`">{{ contactEmail }}</a>
                </div>
              </li>
              <li>
                <i class="fa-regular fa-clock contact-icon"></i>
                <div class="contact-text">
                  <template v-if="businessHours && businessHours.length > 0">
                    <div v-for="(item, index) in businessHours" :key="index">
                      {{ formatDays(item.days) }}: {{ item.hours }}
                      <br v-if="index < businessHours.length - 1" />
                    </div>
                  </template>
                  <template v-else>
                    Mon - Fri: 9:00 AM - 6:00 PM<br />
                    Saturday: 9:00 AM - 2:00 PM
                  </template>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Footer Bottom -->
    <div class="footer-bottom">
      <div class="footer-container">
        <div class="footer-disclaimer">
          <strong>Disclaimer:</strong>
          Investors are advised to conduct their own due diligence and seek independent legal and financial advice before making any investment or commitment based on materials published by Franchise Brigade or its group companies. Franchise Brigade does not guarantee or verify any claims, projections, or commitments made by the franchisor and shall not be held liable for any losses or consequences arising from such claims.
        </div>
        <div class="footer-bottom-content">
          <div class="footer-copyright">
            <span>&copy; {{ new Date().getFullYear() }} {{ siteName }}. All rights reserved.</span>
          </div>
          <div class="footer-legal">
            <a v-if="privacyPolicy" :href="`/${privacyPolicy.slug}`">{{ privacyPolicy.title }}</a>
            <a v-if="termsOfService" :href="`/${termsOfService.slug}`">{{ termsOfService.title }}</a>
            <a v-if="sitemap" :href="`/${sitemap.slug}`">{{ sitemap.title }}</a>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { onMounted, computed } from 'vue'
import { useSettingsStore } from '@/stores/settingsStore'
import { useContactInfoStore } from '@/stores/contactInfoStore'
import { useBusinessHoursStore } from '@/stores/businessHoursStore'
import { usePagesStore } from '@/stores/pagesStore'

const settingsStore = useSettingsStore()
const contactInfoStore = useContactInfoStore()
const businessHoursStore = useBusinessHoursStore()
const pagesStore = usePagesStore()

onMounted(() => {
  settingsStore.fetchSettings()
  contactInfoStore.fetchContactInfo()
  businessHoursStore.fetchBusinessHours()
  if (!pagesStore.pages.length && !pagesStore.loading) {
    pagesStore.fetchPages()
  }
})

const siteName = computed(() => settingsStore.settings?.site_name || 'Franchise Brigade')
const siteDescription = computed(() => settingsStore.settings?.site_description || 'Connecting entrepreneurs with profitable franchise opportunities across India.')
const contactEmail = computed(() => contactInfoStore.contactInfo?.email || 'info@franchisebrigade.com')
const contactPhone = computed(() => contactInfoStore.contactInfo?.phone || '+91 9606943373')
const contactAddress = computed(() => contactInfoStore.contactInfo?.address || 'Janardhan Towers, Residency Road, Above IOB Bank, Opp Bishop Cotton Boys School, Bangalore - 560025')
const siteLogo = computed(() => settingsStore.settings?.site_logo ? settingsStore.settings.site_logo : require('@/assets/logo.png'))
const facebook = computed(() => settingsStore.settings?.social_facebook || '#')
const twitter = computed(() => settingsStore.settings?.social_twitter || '#')
const instagram = computed(() => settingsStore.settings?.social_instagram || '#')
const linkedin = computed(() => settingsStore.settings?.social_linkedin || '#')

// Business hours for footer
const businessHours = computed(() => {
  return businessHoursStore.groupedBusinessHours || []
})

// Page links for footer
const privacyPolicy = computed(() => pagesStore.pageBySlug('privacy-policy'))
const termsOfService = computed(() => pagesStore.pageBySlug('terms-of-service'))
const sitemap = computed(() => pagesStore.pageBySlug('sitemap'))

// Helper function to format days
const formatDays = (days) => {
  if (!days || !Array.isArray(days) || days.length === 0) return ''
  if (days.length === 1) return dayShort(days[0])
  
  const weekOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  const sorted = days.slice().sort((a, b) => weekOrder.indexOf(a) - weekOrder.indexOf(b))
  
  const result = []
  let start = 0
  for (let i = 1; i <= sorted.length; i++) {
    if (i === sorted.length || weekOrder.indexOf(sorted[i]) !== weekOrder.indexOf(sorted[i-1]) + 1) {
      if (start === i-1) result.push(dayShort(sorted[start]))
      else result.push(`${dayShort(sorted[start])} - ${dayShort(sorted[i-1])}`)
      start = i
    }
  }
  return result.join(', ')
}

const dayShort = (day) => {
  const map = {
    Monday: 'Mon',
    Tuesday: 'Tue',
    Wednesday: 'Wed',
    Thursday: 'Thu',
    Friday: 'Fri',
    Saturday: 'Sat',
    Sunday: 'Sun'
  }
  return map[day] || day
}

// Function to scroll to top when navigating
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}
</script>

<style scoped>
.footer-section {
  background: linear-gradient(135deg, #1a1d2e 0%, #232634 100%);
  color: #e6e8ef;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  margin-top: 0;
  position: relative;
}

.footer-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, #ff6b35 50%, transparent 100%);
}

.footer-main {
  padding: 80px 0 50px 0;
}

.footer-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 48px;
}

.footer-grid {
  display: grid;
  grid-template-columns: 2.2fr 1.2fr 1.2fr 1.8fr;
  gap: 80px;
  align-items: start;
}

.footer-col {
  min-width: 0;
}

.footer-logo-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.footer-logo {
  width: 50px;
  height: 50px;
  object-fit: contain;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(44,62,80,0.12);
  padding: 4px;
}

.footer-brand-name {
  font-size: 2.4rem;
  font-weight: 900;
  color: #fff;
  letter-spacing: 0.4px;
  font-family: inherit;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.footer-tagline {
  color: #c7cad9;
  font-size: 1.4rem;
  margin: 0 0 28px 0;
  line-height: 1.7;
  font-family: inherit;
  font-weight: 500;
}

.footer-social {
  display: flex;
  gap: 18px;
  margin-top: 24px;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(255,107,53,0.1);
  color: #ff6b35;
  border-radius: 50%;
  text-decoration: none;
  font-size: 1.3rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(255,107,53,0.2);
}

.social-link:hover {
  background: #ff6b35;
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255,107,53,0.3);
}

.footer-heading {
  color: #fff;
  font-size: 1.6rem;
  font-weight: 900;
  margin-bottom: 36px;
  letter-spacing: 0.3px;
  font-family: inherit;
  position: relative;
}

.footer-heading::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 45px;
  height: 4px;
  background: linear-gradient(90deg, #ff6b35, #ff8c42);
  border-radius: 2px;
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links li {
  margin-bottom: 22px;
  font-size: 1.3rem;
  font-family: inherit;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.footer-links a {
  color: #e6e8ef;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.3rem;
  font-family: inherit;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 4px 0;
  border-radius: 4px;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}

.footer-links a i {
  font-size: 1.1rem;
  width: 20px;
  text-align: center;
  color: #ff6b35;
  transition: all 0.3s ease;
}

.footer-links a:hover {
  color: #ff6b35;
  text-shadow: 0 2px 8px rgba(255,107,53,0.15);
}

.footer-links a:hover::before {
  width: 100%;
}

.footer-links a:hover i {
  transform: scale(1.1);
}

.footer-contact {
  list-style: none;
  padding: 0;
  margin: 0;
  color: #c7cad9;
  font-size: 1.25rem;
  font-family: inherit;
  font-weight: 500;
}

.footer-contact li {
  margin-bottom: 28px;
  display: flex;
  align-items: flex-start;
  gap: 18px;
  line-height: 1.7;
}

.contact-icon {
  color: #ff6b35;
  font-size: 1.4em;
  margin-top: 4px;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,107,53,0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
}

.contact-text {
  flex: 1;
  font-size: 1.25rem;
}

.contact-text a {
  color: #c7cad9;
  text-decoration: none;
  transition: color 0.3s ease;
  font-size: 1.25rem;
}

.contact-text a:hover {
  color: #ff6b35;
}

.footer-bottom {
  border-top: 1px solid #2e3142;
  padding: 28px 0 16px 0;
  background: rgba(0,0,0,0.1);
}

.footer-disclaimer {
  max-width: 1120px;
  margin: 0 auto 24px auto;
  color: #bfc2d1;
  font-size: 1.05rem;
  line-height: 1.7;
  letter-spacing: 0.2px;
  text-align: center;
}

.footer-disclaimer strong {
  color: #fff;
  font-weight: 800;
}

.footer-bottom-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.footer-copyright {
  color: #bfc2d1;
  font-size: 1.25rem;
  letter-spacing: 0.2px;
  font-family: inherit;
  font-weight: 600;
}

.footer-legal {
  display: flex;
  gap: 32px;
  align-items: center;
}

.footer-legal a {
  color: #bfc2d1;
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: 600;
  transition: color 0.3s ease;
  padding: 4px 0;
}

.footer-legal a:hover {
  color: #ff6b35;
}

@media (max-width: 1200px) {
  .footer-container {
    padding: 0 24px;
  }
  .footer-grid {
    gap: 50px;
  }
}

@media (max-width: 900px) {
  .footer-main {
    padding: 50px 0 30px 0;
  }
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 40px;
  }
  .footer-brand {
    grid-column: 1 / -1;
  }
  .footer-bottom-content {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
  .footer-brand-name {
    font-size: 2rem;
  }
  .footer-tagline {
    font-size: 1.2rem;
  }
  .footer-heading {
    font-size: 1.4rem;
  }
  .footer-links a,
  .footer-links li {
    font-size: 1.15rem;
  }
  .footer-contact {
    font-size: 1.15rem;
  }
  .contact-text,
  .contact-text a {
    font-size: 1.15rem;
  }
  .footer-copyright {
    font-size: 1.15rem;
  }
  .footer-disclaimer {
    font-size: 1rem;
  }
  .footer-legal a {
    font-size: 1.1rem;
  }
}

@media (max-width: 700px) {
  .footer-main {
    padding: 30px 0 20px 0;
  }
  .footer-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  .footer-logo-row {
    margin-bottom: 16px;
  }
  .footer-bottom {
    font-size: 1rem;
    padding: 20px 0 12px 0;
  }
  .footer-brand-name {
    font-size: 1.8rem;
  }
  .footer-tagline {
    font-size: 1.1rem;
  }
  .footer-heading {
    font-size: 1.3rem;
  }
  .footer-links a,
  .footer-links li {
    font-size: 1.1rem;
  }
  .footer-contact {
    font-size: 1.1rem;
  }
  .contact-text,
  .contact-text a {
    font-size: 1.1rem;
  }
  .footer-copyright {
    font-size: 1.1rem;
  }
  .footer-disclaimer {
    font-size: 0.95rem;
    text-align: left;
  }
  .footer-legal a {
    font-size: 1rem;
  }
}
</style> 
